import { useEffect, useState } from 'react';
import PushNotificationService from '../services/pushNotifications';

interface PWAInstallPrompt {
  prompt: () => Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  installPrompt: PWAInstallPrompt | null;
  showInstallBanner: boolean;
}

export const usePWA = () => {
  const [state, setState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isOnline: navigator.onLine,
    installPrompt: null,
    showInstallBanner: false,
  });

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      const isInstalled = 
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://');
      
      setState(prev => ({ ...prev, isInstalled }));
    };

    checkIfInstalled();

    // Handle install prompt
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setState(prev => ({
        ...prev,
        isInstallable: true,
        installPrompt: {
          prompt: async () => {
            e.prompt();
            const choice = await e.userChoice;
            setState(prev => ({
              ...prev,
              isInstallable: false,
              installPrompt: null,
              showInstallBanner: false
            }));
            return { outcome: choice.outcome };
          }
        },
        showInstallBanner: true
      }));
    };

    // Handle app installed
    const handleAppInstalled = () => {
      setState(prev => ({
        ...prev,
        isInstalled: true,
        isInstallable: false,
        installPrompt: null,
        showInstallBanner: false
      }));
      console.log('PWA was installed');
    };

    // Handle online/offline status
    const handleOnline = () => setState(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () => setState(prev => ({ ...prev, isOnline: false }));

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initialize push notifications
    PushNotificationService.initialize().catch(console.error);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const installApp = async (): Promise<boolean> => {
    if (!state.installPrompt) return false;
    
    try {
      const result = await state.installPrompt.prompt();
      return result.outcome === 'accepted';
    } catch (error) {
      console.error('Error installing app:', error);
      return false;
    }
  };

  const dismissInstallBanner = () => {
    setState(prev => ({ ...prev, showInstallBanner: false }));
  };

  return {
    ...state,
    installApp,
    dismissInstallBanner,
    pushNotifications: PushNotificationService
  };
};